import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { Typography } from "@mui/material";

import { faRotate, faSliders } from "@fortawesome/pro-regular-svg-icons";

import { FetchBaseQueryError } from "@reduxjs/toolkit/query";

import { Icon, Modal } from "@/components";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";

import { authSelector } from "@/pages/Public/redux/auth.slice";
import { useAppSelector } from "@/redux/hooks";

import { useReconnectLinkedinMutation } from "@/pages/Private/redux/unipile/unipile.api";

import { isLinkedinDisconnectedStatus } from "@/pages/Private/pages/Account/schema/unipile";

import { useSenderDeletion } from "../hooks/useSenderDeletion";
import { useUnipileConnect } from "../hooks/useUnipileConnect";
import { PendingSchedulesDialog } from "./PendingSchedulesDialog";
import { SettingsSubhead, SettingsSubBlock } from "./SettingsSection";
import { LinkedinSettingsModal } from "./LinkedinSettingsModal";

export const RECONNECT_LINKEDIN_PARAM = "reconnect";
export const RECONNECT_LINKEDIN_VALUE = "linkedin";

/**
 * "Connection" section of the LinkedIn card: the login status row + connect / reconnect /
 * disconnect actions and their confirmation modals. Rendered card-less so it can sit inside
 * the shared LinkedIn `SettingsCard` alongside the syncing section.
 */
export const LinkedinLogin: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`account.connectedAccounts.${key}`);

	const { user } = useAppSelector(authSelector);

	const [showLogoutFromLinkedinModal, setShowLogoutFromLinkedinModal] = useState<boolean>(false);
	const [reconnectLinkedin, { isLoading: isReconnectLoading }] = useReconnectLinkedinMutation();

	const {
		isLoading,
		error,
		setError,
		showConfirmModal,
		setShowConfirmModal,
		startValidation,
		setStartValidation,
		connect,
	} = useUnipileConnect({ type: "linkedin" });

	const {
		initiateDeletion,
		confirmDeletion,
		cancelDeletion,
		showPendingDialog,
		totalCount,
		campaigns,
		isCheckingPending,
		isDeleting,
	} = useSenderDeletion({
		senderType: "linkedin",
		onSuccess: () => setShowLogoutFromLinkedinModal(false),
	});

	const [showReconnectModal, setShowReconnectModal] = useState<boolean>(false);
	const [showSettingsModal, setShowSettingsModal] = useState<boolean>(false);

	const [searchParams, setSearchParams] = useSearchParams();

	const isLoggedIn = !!user?.unipile?.isLoggedIn;

	useEffect(() => {
		if (searchParams.get(RECONNECT_LINKEDIN_PARAM) !== RECONNECT_LINKEDIN_VALUE) {
			return;
		}

		// A previously-connected account that is now logged out (`CREDENTIALS`) — including
		// when only Sales Navigator dropped, where the base account stays logged in — must
		// reconnect (its `accountId` still exists). Only a truly never-connected account
		// goes through the fresh connect flow.
		if (user?.unipile?.isLoggedIn || isLinkedinDisconnectedStatus(user?.unipile?.status)) {
			setShowReconnectModal(true);
		} else if (user?.linkedinAllowed) {
			setShowConfirmModal(true);
		}

		const next = new URLSearchParams(searchParams);

		next.delete(RECONNECT_LINKEDIN_PARAM);
		setSearchParams(next, { replace: true });
	}, [
		searchParams,
		user?.unipile?.isLoggedIn,
		user?.unipile?.status,
		user?.linkedinAllowed,
		setSearchParams,
	]);

	const handleReconnectLinkedin = async () => {
		try {
			const response = await reconnectLinkedin({
				successRedirectUrl: window.location.href || undefined,
			}).unwrap();

			if (response.url) {
				window.open(response.url, "_blank");
				setShowReconnectModal(false);
			}
		} catch (error) {
			setError(((error as FetchBaseQueryError).data as { message: string }).message);
		}
	};

	const handleLogoutFromLinkedin = async () => {
		await initiateDeletion();
	};

	return (
		<div>
			<SettingsSubhead>{ts("connectionSubhead")}</SettingsSubhead>

			<SettingsSubBlock className="mt-2">
				<div className="flex flex-wrap items-center gap-x-4 gap-y-3">
					<div className="min-w-0 flex-1">
						<div className="text-sm font-semibold text-text">{ts("loginWithLinkedin")}</div>
						<div className="mt-1.5 flex items-center gap-2.5">
							<span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md bg-[#0A66C2] text-xs font-bold text-white">
								in
							</span>
							<span className="truncate text-sm text-text">
								{isLoggedIn ? (
									<>
										{user?.email}
										<span className="text-text-meta"> · {ts("connected")}</span>
									</>
								) : (
									<span className="text-text-meta">{ts("notConnected")}</span>
								)}
							</span>
						</div>
					</div>

					<div className="flex flex-shrink-0 items-center gap-3">
						{isLoggedIn ? (
							<>
								<Button
									color={ButtonColor.ACTION_SECONDARY}
									image={<Icon className="mr-2 h-3.5 w-3.5" icon={faSliders} />}
									size={ButtonSize.S}
									title={ts("editLinkedinSettings")}
									type="button"
									onClick={() => setShowSettingsModal(true)}
								/>
								<Button
									color={ButtonColor.ACTION_SECONDARY}
									image={<Icon className="mr-2 h-3.5 w-3.5" icon={faRotate} />}
									size={ButtonSize.S}
									title={ts("reconnect")}
									type="button"
									onClick={() => setShowReconnectModal(true)}
								/>
								<Button
									color={ButtonColor.DANGER_SECONDARY}
									size={ButtonSize.S}
									title={ts("disconnect")}
									type="button"
									onClick={() => setShowLogoutFromLinkedinModal(true)}
								/>
							</>
						) : user?.linkedinAllowed ? (
							<Button
								color={ButtonColor.ACTION}
								size={ButtonSize.S}
								title={ts("connectLinkedin")}
								type="button"
								onClick={() => setShowConfirmModal(true)}
							/>
						) : (
							<span className="text-[12.5px] text-text-meta">{ts("noLinkedinPermission")}</span>
						)}
					</div>
				</div>
			</SettingsSubBlock>

			<Modal
				disableSave={!user?.unipile?.isLoggedIn}
				handleClose={async () => {
					setStartValidation(false);
				}}
				handleSave={() => {
					setStartValidation(false);
				}}
				isLoading={isLoading}
				isOpened={startValidation}
				size="sm"
				submitButtonText={"Done"}
				title={
					!user?.unipile?.isLoggedIn ? "Currently logging in to LinkedIn" : "Successfully logged in"
				}
			>
				{!user?.unipile?.isLoggedIn ? (
					<div className="text-text-meta text-sm">
						Please wait while we validate your LinkedIn account. This may take a few seconds.
					</div>
				) : (
					<div className="text-text-meta text-sm">You are currently logged in to LinkedIn.</div>
				)}
			</Modal>
			<Modal
				handleClose={
					!error
						? async () => {
								setShowConfirmModal(false);
							}
						: undefined
				}
				handleSave={() => {
					if (error) {
						setError(null);
						setShowConfirmModal(false);
					} else {
						connect();
					}
				}}
				isLoading={isLoading}
				isOpened={showConfirmModal}
				size="sm"
				submitButtonText={error ? "Close" : "Start LinkedIn login"}
				title={"Login with your LinkedIn Account"}
			>
				{error && <div className="text-text-meta text-sm">{error}</div>}
				{!error && (
					<>
						<div className="text-text-meta text-sm">Confirm this modal to login with LinkedIn.</div>
						<br />
						<div className="text-text-meta text-sm">
							By clicking on the button, we will open a new tab to login with LinkedIn. <br />
							<span className="font-bold">Popups need to be allowed in your browser.</span>
						</div>
					</>
				)}
			</Modal>

			<Modal
				handleClose={
					!error
						? async () => {
								setShowReconnectModal(false);
							}
						: undefined
				}
				handleSave={() => {
					if (error) {
						setError(null);
						setShowReconnectModal(false);
					} else {
						handleReconnectLinkedin();
					}
				}}
				isLoading={isReconnectLoading}
				isOpened={showReconnectModal}
				size="sm"
				submitButtonText={error ? "Close" : "Reconnect LinkedIn"}
				title={"Reconnect your LinkedIn Account"}
			>
				{error && <div className="text-text-meta text-sm">{error}</div>}
				{!error && (
					<>
						<div className="text-text-meta text-sm">
							Confirm this modal to reconnect your LinkedIn account.
						</div>
						<br />
						<div className="text-text-meta text-sm">
							By clicking on the button, we will open a new tab to re-authenticate with LinkedIn.{" "}
							<br />
							<span className="font-bold">Popups need to be allowed in your browser.</span>
						</div>
					</>
				)}
			</Modal>

			<Modal
				handleClose={() => setShowLogoutFromLinkedinModal(false)}
				handleSave={() => {
					handleLogoutFromLinkedin();
				}}
				isLoading={isDeleting || isCheckingPending}
				isOpened={showLogoutFromLinkedinModal}
				title="Logout from LinkedIn"
			>
				<div>
					<Typography variant="h6">
						Are you sure you want to logout from LinkedIn? This will remove your LinkedIn profile
						from our system. You will need to login again to continue to scrape or automate on
						linkedin.
					</Typography>
				</div>
			</Modal>

			<PendingSchedulesDialog
				campaigns={campaigns}
				isLoading={isDeleting}
				isOpen={showPendingDialog}
				onClose={cancelDeletion}
				onConfirm={confirmDeletion}
				senderType="linkedin"
				totalCount={totalCount}
			/>

			{showSettingsModal && (
				<LinkedinSettingsModal
					connectionRequestsPerDay={user?.connectionRequestsPerDay ?? 0}
					handleClose={() => setShowSettingsModal(false)}
					isVisible={showSettingsModal}
					messagesPerDay={user?.messagesPerDay ?? 0}
					visitsPerDay={user?.visitsPerDay ?? 0}
				/>
			)}
		</div>
	);
};
